import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, CircularProgress, Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useQuery } from '@tanstack/react-query';
import PartnersSection from '../Home/components/PartnersSection';
import useWindowDimensions from '../../../Reusables/VisualAndAccord/Hooks/useWindowDimensions';
import PageHeaders from '../../../Utils/PageHeaders';
import HubNav from '../../ResourceHubPages/HubNav/HubNav';

// API
import { fetchEmployerData } from './api';

// Sub-components
import PlanHeader from './components/PlanHeader';
import PlanQuickFacts from './components/PlanQuickFacts';
import PlanAnalysis from './components/PlanAnalysis';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#FFFFFF',
    paddingBottom: theme.spacing(8),
  },
  loaderContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
  },
}));

const Employer401k = () => {
  const classes = useStyles();
  const { employerName } = useParams();
  const navigate = useNavigate();
  const { width } = useWindowDimensions();
  const [strategy, setStrategy] = useState(2); // Moderate by default

  const { data: employerData, isLoading, error } = useQuery({
    queryKey: ['employer401k', employerName],
    queryFn: () => fetchEmployerData(employerName),
    enabled: !!employerName,
    onError: (err) => {
      console.error('Error fetching employer 401k data:', err);
      navigate('/404');
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
    cacheTime: 1000 * 60 * 60, // 1 hour
  });

  if (isLoading) {
    return (
      <Box className={classes.loaderContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !employerData) {
    return null;
  }

  return (
    <>
    <HubNav />
    <div className={classes.root}>
      <PageHeaders
        title={`${employerData.employer_name} 401(k) Plan Details | Plootus`}
        description={`Explore ${employerData.employer_name}'s 401(k) plan, including employer match, vesting schedule, fees, fund options, and performance insights.`}
        keywords={`401k, ${employerData.employer_name}, retirement plan, 401k plan, employer match, vesting schedule`}
        canonicalUrl={`https://www.plootus.com/401k/${employerName}`}
      />
      <Container maxWidth="lg" sx={{ mt: 2 }}>
        <PlanHeader 
          planName={employerData.employer_name} 
          rating={employerData.plan_rating} 
        />

        <PlanQuickFacts 
          employerName={employerName} 
          match={{
            subHeader: employerData.employer_match_sub_header,
            description: employerData.employer_match_description,
            rating: employerData.employer_match_star_rating
          }} 
          vesting={{
            subHeader: employerData.vesting_schedule_sub_header,
            description: employerData.vesting_schedule_description,
            rating: employerData.vesting_schedule_star_rating
          }} 
        />

        <PlanAnalysis 
          planType={employerData.plan_type} 
          strategy={strategy} 
          setStrategy={setStrategy} 
          width={width} 
          funds={employerData.allocations} 
          fees={employerData.fees}
          createdAt={employerData.createdAt}
        />
      </Container>
      <PartnersSection />
    </div>
    </>
  );
};

export default Employer401k;
